import * as React from 'react';
import styled, { css } from 'styled-components';

import { AquaTheme as T } from '../AquaTheme';
import { IDataset } from '../../../types/IDataset';

interface IProps {
  /** @ignore */
  className?: string;
  /** Dataset to show in menu item. */
  data: IDataset;
  /**
   * Is the item part of an exclusive group? Then it shows a radio indicator
   * rather than a switch.
   */
  exclusive?: boolean;
  /** Fired when menu item is clicked. */
  onClick: () => void;
}

const AquaMenuItemBase = (props: IProps) => {
  const handleClick = (e: React.MouseEvent) => {
    // Must stop propagation for menu not to close itself:
    e.stopPropagation();
    props.onClick();
  }

  return (
    <button
      type="button"
      className={props.className}
      role={props.exclusive ? "radio" : "switch"}
      aria-checked={!!props.data.active}
      onClick={handleClick}
    >
      <TextBlock>
        <Title>{props.data.title}</Title>
        <Description>{props.data.description ?? props.data.title}</Description>
      </TextBlock>
      {props.exclusive ? <Radio/> : <Switch/>}
    </button>
  )
}

const Title = styled.div`
  font: ${T.font.labelLarge};
  color: ${T.colors.neutral[100]};
`

const Description = styled.div`
  font: ${T.font.bodySmall};
  color: ${T.colors.neutral[80]};
  margin-top: 2px;
`

const TextBlock = styled.div`
  flex: 1;
  min-width: 0;
`

// On/off switch, drawn with a knob in :after.
const Switch = styled.div`
  position: relative;
  flex: none;
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: ${T.colors.primary[5]};
  box-shadow: ${T.shadows.inner};
  transition: background-color ease-in-out ${T.duration}ms;
  &:after {
    content: '';
    position: absolute;
    left: 3px;
    top: 3px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${T.colors.neutral[50]};
    transition: transform ease-out 150ms, background-color ease-in-out ${T.duration}ms;
  }
`

const Radio = styled.div`
  position: relative;
  flex: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  box-sizing: border-box;
  border: solid 2px ${T.colors.neutral[50]};
  transition: border-color ease-in-out ${T.duration}ms;
  &:after {
    content: '';
    position: absolute;
    left: 3px;
    top: 3px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${T.colors.primary[1]};
    transform: scale(0);
    transition: transform ease-out 150ms;
  }
`

const AquaMenuItem = styled(AquaMenuItemBase)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 12px 16px;
  box-sizing: border-box;
  border: none;
  border-radius: ${T.radius.normal}px;
  background-color: rgba(28,36,42,0.35);
  text-align: left;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition: background-color ease-in-out ${T.duration}ms;

  // Active indicator bar on the left edge:
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${T.colors.primary[1]};
    transform: scaleX(0);
    transform-origin: 0 0;
    transition: transform ease-out 150ms;
  }

  &:hover {
    background-color: rgba(28,36,42,0.6);
  }
  &:focus-visible {
    outline: solid 2px ${T.colors.neutral[100]};
    outline-offset: -2px;
  }

  ${p => p.data.active && css`
    background-color: ${T.colors.primary[3]};
    &:hover {
      background-color: ${T.colors.primary[4]};
    }
    &:before {
      transform: scaleX(1);
    }
    ${Switch} {
      background: ${T.colors.primary[1]};
      &:after {
        background: ${T.colors.neutral[100]};
        transform: translateX(16px);
      }
    }
    ${Radio} {
      border-color: ${T.colors.primary[1]};
      &:after {
        transform: scale(1);
      }
    }
  `}
`

export { AquaMenuItem }
